import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link } from "react-router-dom";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Global safety net.
 *
 * Without this, any uncaught error thrown while rendering a route unmounts the
 * whole React tree and leaves the visitor staring at a blank white page with no
 * way back. This catches the crash, shows a friendly recoverable screen, and
 * lets the visitor reload or return home instead of getting stuck.
 */
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface the real error for debugging without breaking the experience.
    console.error("Unhandled UI error:", error, info);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleHome = (): void => {
    const goHome = window.REACT_APP_NAVIGATE;
    if (typeof goHome === "function") {
      this.setState({ hasError: false });
      goHome("/");
      return;
    }
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50 px-6 py-20">
        <div className="w-full max-w-md text-center">
          <span className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-700">
            <i className="ri-error-warning-line text-3xl" />
          </span>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">
            Something went wrong
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            We hit an unexpected error while loading this page. Your data is safe — reload the page to
            pick up right where you left off.
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
            >
              <i className="ri-refresh-line text-base" /> Reload page
            </button>
            <Link
              to="/"
              onClick={this.handleHome}
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 sm:w-auto"
            >
              <i className="ri-home-4-line text-base" /> Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }
}